import { IAfterGuiAttachedParams } from "ag-grid-enterprise";
import { CustomFilterDisplayProps, useGridFilterDisplay } from "ag-grid-react";
import {
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  forwardRef
} from "react";

import { Checkbox } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import Tree, { DataNode } from "antd/lib/tree";

import { CheckboxChangeEvent } from "antd/lib/checkbox";

const dataList: { key: React.Key; title: string }[] = [];

const getParentKey = (key: React.Key, tree: DataNode[]): React.Key => {
  let parentKey: React.Key;
  for (let i = 0; i < tree.length; i += 1) {
    const node = tree[i];
    if (node.children) {
      if (node.children.some((item) => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }
  return parentKey!;
};

const generateList = (data: DataNode[]) => {
  for (let i = 0; i < data.length; i += 1) {
    const node = data[i];
    const { key } = node;
    dataList.push({ key, title: key as string });
    if (node.children) {
      generateList(node.children);
    }
  }
};

const flattenTree = (nodes: DataNode[]) => {
  let map: Record<string, DataNode> = {};
  nodes.forEach((node) => {
    map[String(node.key)] = node;
    if (node.children) {
      map = { ...map, ...flattenTree(node.children) };
    }
  });
  return map;
};

const getAllDescendantKeys = (node: DataNode) => {
  let keys: string[] = [];
  if (node.children) {
    node.children.forEach((child) => {
      keys.push(child.key as string);
      keys = keys.concat(getAllDescendantKeys(child));
    });
  }
  return keys;
};

const getAllKeys = (nodes: DataNode[]) => {
  let keys: string[] = [];
  nodes.forEach((node) => {
    keys.push(node.key as string);
    if (node.children) {
      keys = keys.concat(getAllKeys(node.children));
    }
  });
  return keys;
};

const areAllNodesChecked = (checkedKeys: Set<any>, treeData: any) => {
  const allKeys = getAllKeys(treeData);
  return allKeys.every((key: any) => checkedKeys.has(key));
};

const BLANKS_KEY = "(Blanks)";
const SELECT_ALL_KEY = "(Select All)";

export const CustomTreeFilterComponent = forwardRef(
  (
    { onModelChange, api }: CustomFilterDisplayProps,
    ref: React.ForwardedRef<any>
  ) => {
    const refInput = useRef<HTMLInputElement>(null);
    const [treeFilterStructure, setTreeFilterStructure] = useState<DataNode[]>(
      []
    );
    const [treeFilterSelectedNodes, setTreeFilterSelectedNodes] = useState<
      React.Key[]
    >([]);

    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState("");
    const [autoExpandParent, setAutoExpandParent] = useState(true);

    const [checkAll, setCheckAll] = useState(true);

    const [nodeMap, setNodeMap] = useState<Record<string, DataNode>>({});

    useImperativeHandle(ref, () => ({
      setDataForFilter: (data: any) => {
        setTreeFilterStructure(data.treeFilterStructure);
        setTreeFilterSelectedNodes(data.treeFilterSelectedNodes);
      }
    }));

    const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
      if (!params || !params.suppressFocus) {
        // Focus the input element for keyboard navigation.
        // Can't do this in an effect,
        // as the component is not recreated when hidden and then shown again

        window.setTimeout(() => {
          refInput.current?.focus();
        });
      }
    }, []);

    // register filter handlers with the grid
    useGridFilterDisplay({
      afterGuiAttached
    });

    const treeData = useMemo(() => {
      const loop = (data: DataNode[]): DataNode[] =>
        data.map((item) => {
          const strTitle = item.title as string;

          const index = strTitle
            .toLowerCase()
            .indexOf(searchValue.toLowerCase());
          const title =
            searchValue && index > -1 ? (
              <span
                className="site-tree-search-value"
                style={{ fontWeight: "600" }}
              >
                {strTitle}
              </span>
            ) : (
              <span>{strTitle}</span>
            );

          if (item.children) {
            return { title, key: item.key, children: loop(item.children) };
          }

          return {
            title,
            key: item.key
          };
        });

      const tmpTreeData = loop(treeFilterStructure);

      tmpTreeData.sort((a, b) => {
        const titleA = (a.key as string).toLowerCase();
        const titleB = (b.key as string).toLowerCase();
        return titleA.localeCompare(titleB);
      });

      return tmpTreeData;
    }, [treeFilterStructure, searchValue]);

    useEffect(() => {
      // treeData useMemo calls loop(treeFilterStructure) every render, so a
      // falsy treeFilterStructure crashes render before this effect's else runs.
      /* v8 ignore next */
      if (treeFilterStructure) {
        generateList(treeFilterStructure);
        setNodeMap(flattenTree(treeFilterStructure));
      }
    }, [treeFilterStructure]);

    const onSearch: any = (value: string) => {
      let newExpandedKeys: any = [];

      if (value.length > 0) {
        newExpandedKeys = dataList
          .map((item) => {
            if (item.title.toLowerCase().indexOf(value.toLowerCase()) > -1) {
              return getParentKey(item.key, treeFilterStructure);
            }
            return null;
          })
          .filter((item, i, self) => item && self.indexOf(item) === i);
      }
      setExpandedKeys(newExpandedKeys);
      setSearchValue(value);
      setAutoExpandParent(true);
    };

    const onExpand = (newExpandedKeys: React.Key[]) => {
      setExpandedKeys(newExpandedKeys);
      setAutoExpandParent(false);
    };

    const onTreeItemCheck = (_checkedInfo: any, info: any) => {
      const { node, checked } = info;
      const newCheckedKeys = new Set(checkedKeys);

      setCheckAll(false);

      if (checked) {
        newCheckedKeys.add(node.key);

        const descendants = getAllDescendantKeys(nodeMap[node.key]);
        descendants.forEach((k) => newCheckedKeys.add(k));
      } else {
        newCheckedKeys.delete(node.key);
        const descendants = getAllDescendantKeys(nodeMap[node.key]);
        descendants.forEach((k) => newCheckedKeys.delete(k));
      }

      setCheckedKeys(Array.from(newCheckedKeys));

      setCheckAll(areAllNodesChecked(newCheckedKeys, treeData));
    };

    const onSelectAllChange = (e: CheckboxChangeEvent) => {
      const allKeys = getAllKeys(treeData);

      if (e.target.checked) {
        setCheckedKeys(allKeys);
      } else {
        setCheckedKeys([]);
      }

      setCheckAll(e.target.checked);
    };

    const applySavedFilters = useCallback(() => {
      const colProps = api.getFilterModel();
      const cKeys = colProps?.locations?.values;

      if (!cKeys) {
        setCheckAll(true);
        setCheckedKeys(getAllKeys(treeData));
      } else {
        const incomingSelectedKeys = cKeys?.map((key: any) =>
          key === BLANKS_KEY ? "" : key
        );

        setCheckedKeys(incomingSelectedKeys);

        setCheckAll(false);
      }
    }, [api, treeData]);

    useEffect(
      function onComponentMount() {
        applySavedFilters();
      },
      [applySavedFilters]
    );

    useEffect(() => {
      if (checkAll) {
        onModelChange(null);
      } else {
        const replacedBlankKeys = checkedKeys.map((key: any) =>
          key === "" ? BLANKS_KEY : key
        );
        onModelChange({
          // Unreachable: this else-branch only runs when checkAll is already false.
          /* v8 ignore next */
          values: checkAll ? [SELECT_ALL_KEY] : replacedBlankKeys,
          filterType: "set",
          type: "in"
        });
      }
    }, [treeFilterSelectedNodes, checkedKeys, onModelChange, checkAll]);

    return (
      <div className="h-[300px] w-[270px] p-2 bg-ciq-gray-100 flex flex-col">
        <div className="pb-1">
          <CiqAgSearch
            placeholder="Search"
            onSearch={onSearch}
            className="w-full mb-1"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            data-testid="treefilter-input-search"
          />
        </div>
        <div className="flex-1 overflow-auto">
          <div className="ml-6 mb-1.5">
            <Checkbox
              onChange={onSelectAllChange}
              checked={checkAll}
              style={{ fontSize: "13px" }}
              data-testid="treefilter-checkbox-select-all"
            >
              &nbsp;(Select All)
            </Checkbox>
          </div>
          <Tree
            checkable
            selectable={false}
            draggable={false}
            onExpand={onExpand}
            autoExpandParent={autoExpandParent}
            expandedKeys={expandedKeys}
            onCheck={onTreeItemCheck}
            checkedKeys={checkedKeys}
            treeData={treeData}
            className="bg-transparent"
            checkStrictly
          />
        </div>
      </div>
    );
  }
);

CustomTreeFilterComponent.displayName = "CustomTreeFilterComponent";
